class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.giftEligibilityCheckInFlight = null;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link || link.closest('cart-drawer')) return;

      const linkUrl = new URL(link.href, window.location.origin);
      const cartUrl = new URL(routes.cart_url, window.location.origin);
      if (linkUrl.pathname !== cartUrl.pathname) return;

      event.preventDefault();
      this.open(link);
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.enforceGiftEligibilityFromCart();

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getFreeGiftThreshold() {
    return Number(this.dataset.freeGiftThreshold || 0);
  }

  isFreeGiftItem(item) {
    return item.properties && item.properties._free_gift === 'true';
  }

  getLinePrice(item) {
    return Number(item.final_line_price ?? item.line_price ?? item.original_line_price ?? item.price * item.quantity ?? 0);
  }

  getGiftLines(cartState) {
    if (!cartState?.items?.length) return [];

    return cartState.items
      .map((item, index) => ({ item, line: index + 1 }))
      .filter(({ item }) => this.isFreeGiftItem(item));
  }

  getQualifyingSubtotal(cartState) {
    if (!cartState?.items?.length) return 0;

    return cartState.items.reduce((subtotal, item) => {
      if (this.isFreeGiftItem(item)) return subtotal;
      return subtotal + this.getLinePrice(item);
    }, 0);
  }

  enforceGiftEligibilityFromCart() {
    if (this.giftEligibilityCheckInFlight) return this.giftEligibilityCheckInFlight;

    this.giftEligibilityCheckInFlight = fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cartState) => this.enforceGiftEligibility(cartState))
      .catch(() => false)
      .finally(() => {
        this.giftEligibilityCheckInFlight = null;
      });

    return this.giftEligibilityCheckInFlight;
  }

  enforceGiftEligibility(cartState) {
    const threshold = this.getFreeGiftThreshold();
    const giftLines = this.getGiftLines(cartState);

    if (!threshold || !giftLines.length || this.getQualifyingSubtotal(cartState) >= threshold) {
      return Promise.resolve(false);
    }

    return this.removeGiftLines(giftLines.map(({ line }) => line));
  }

  removeGiftLines(lines) {
    const sortedLines = [...lines].sort((a, b) => b - a);
    let latestState;

    return sortedLines
      .reduce((request, line) => {
        return request.then(() => {
          const config = fetchConfig();
          config.body = JSON.stringify({
            line,
            quantity: 0,
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          });

          return fetch(routes.cart_change_url, config)
            .then((response) => response.json())
            .then((state) => {
              latestState = state;
            });
        });
      }, Promise.resolve())
      .then(() => {
        if (!latestState) return false;

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-gift-eligibility',
          cartData: latestState,
        });
        this.renderContents(latestState);
        return true;
      });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerProductFormBridge {
  constructor() {
    // Cancel our custom form before Shopify's target listener observes a native
    // submission. The successful AJAX request supplies the cart tracking event.
    document.addEventListener('submit', (event) => {
      if (event.target instanceof HTMLFormElement && event.target.matches('.onpd__form')) {
        this.onSubmit(event);
      }
    }, { capture: true });
    document.addEventListener('submit', this.onSubmit.bind(this));
    document.addEventListener('click', this.onStickyAddClick.bind(this));
  }

  onStickyAddClick(event) {
    const button = event.target.closest('[data-ombre-sticky-button]');
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    const productRoot = button.closest('[data-ombre-native-product-detail]');
    const form = productRoot?.querySelector('form[action*="/cart/add"], .onpd__form');
    if (!form || typeof form.requestSubmit !== 'function') return;

    event.preventDefault();
    form.requestSubmit();
  }

  onSubmit(event) {
    if (event.defaultPrevented) return;

    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !this.isCartAddForm(form)) return;
    if (form.closest('cart-drawer')) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return;
    event.preventDefault();
    if (form.dataset.cartDrawerAdding === 'true') return;

    this.addToCart(form, cartDrawer, event.submitter);
  }

  isCartAddForm(form) {
    const action = form.getAttribute('action') || '';

    return action.includes('/cart/add') && form.querySelector('[name="id"]');
  }

  setLoading(form, submitter, isLoading) {
    const buttons = new Set([
      submitter,
      form.querySelector('[type="submit"]'),
      form.closest('[data-ombre-native-product-detail]')?.querySelector('[data-ombre-sticky-button]'),
    ]);

    buttons.forEach((button) => {
      if (!button) return;
      button.toggleAttribute('aria-disabled', isLoading);
      button.classList.toggle('loading', isLoading);
    });
  }

  addToCart(form, cartDrawer, submitter) {
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(form);
    formData.append(
      'sections',
      cartDrawer.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    cartDrawer.setActiveElement(submitter || document.activeElement);
    config.body = formData;

    form.dataset.cartDrawerAdding = 'true';
    this.setLoading(form, submitter, true);

    fetch(routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || response.message || window.cartStrings.error);
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-product-form-bridge',
          productVariantId: formData.get('id'),
          cartData: response,
        });

        CartPerformance.measure('add:paint-updated-sections', () => {
          cartDrawer.renderContents(response);
        });

        if (window.OmbreAnalytics && window.OmbreAnalytics.trackAddToCart) {
          window.OmbreAnalytics.trackAddToCart(submitter || form, {
            quantity: formData.get('quantity') || '1',
          });
        }
      })
      .catch((error) => {
        const errorTarget =
          form.querySelector('.product-form__error-message') ||
          document.getElementById('CartDrawer-CartErrors');

        if (errorTarget) errorTarget.textContent = error.message || window.cartStrings.error;
      })
      .finally(() => {
        delete form.dataset.cartDrawerAdding;
        this.setLoading(form, submitter, false);
      });
  }
}

new CartDrawerProductFormBridge();

class CartCheckoutBridge {
  constructor() {
    document.addEventListener('click', this.onClick.bind(this));
  }

  onClick(event) {
    const button = event.target.closest('.cart__checkout-button[name="checkout"]');
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    const formId = button.getAttribute('form');
    const form = formId ? document.getElementById(formId) : button.closest('form');
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.ombreCheckoutSubmitting === 'true') return;

    event.preventDefault();
    form.dataset.ombreCheckoutSubmitting = 'true';

    try {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(button);
        return;
      }
    } catch (error) {
      // Fall through to the native submit fallback below.
    }

    const fallbackSubmitter = document.createElement('input');
    fallbackSubmitter.type = 'hidden';
    fallbackSubmitter.dataset.ombreCheckoutSubmit = 'true';
    fallbackSubmitter.name = button.name;
    fallbackSubmitter.value = button.value || 'checkout';
    form.appendChild(fallbackSubmitter);
    form.submit();
  }
}

new CartCheckoutBridge();

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerGiftButton extends HTMLElement {
  constructor() {
    super();

    this.button = this.querySelector('[data-free-gift-variant-id]');
    this.button?.addEventListener('click', this.onClick.bind(this));
  }

  onClick(event) {
    event.preventDefault();

    if (!this.button || this.button.disabled) return;

    const variantId = this.button.getAttribute('data-free-gift-variant-id');
    const cartDrawer = document.querySelector('cart-drawer');
    if (!variantId || !cartDrawer) return;

    const originalText = this.button.textContent;
    this.button.disabled = true;
    this.button.classList.add('is-loading');
    this.closest('.cart-gift-selector')
      ?.querySelectorAll('[data-free-gift-variant-id]')
      .forEach((button) => {
        if (button !== this.button) button.disabled = true;
      });

    const config = fetchConfig('javascript');
    config.body = JSON.stringify({
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: {
            _free_gift: 'true',
          },
        },
      ],
      sections: cartDrawer.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || response.message || window.cartStrings.error);
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-gift-button',
          productVariantId: variantId,
          cartData: response,
        });

        CartPerformance.measure('gift:add:paint-updated-sections', () => {
          cartDrawer.renderContents(response);
        });
      })
      .catch((error) => {
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = error.message || window.cartStrings.error;

        this.button.disabled = false;
        this.button.textContent = originalText;
      })
      .finally(() => {
        this.button.classList.remove('is-loading');
      });
  }
}

customElements.define('cart-drawer-gift-button', CartDrawerGiftButton);
